/*
  NS Side by side: mobile card carousel.

  Deliberately mirrors assets/ns-editorial-gallery.js, which itself mirrors
  ns-full-bleed-carousel.js, with the selectors renamed to this section's own data
  attributes. NOT a shared file, for the reason recorded there: each module binds to
  attributes named for its own section, and renaming them would touch a file another section
  depends on. Same structure, naming and lifecycle, so any of them can be deleted alone.

  Figma: 15244:10098 (mobile, 375). Below 750 the two cards sit in a horizontally scrolling
  track with a pagination row beneath; above it they are the designed two-up grid and this
  module does nothing at all.

  🔴 Bound to the IMAGE-CARD instance only. The Liquid emits [data-ns-sbs] just for a section
  whose every card carries an image, so `ns_side_by_side_booking` — plain cards, and the one
  holding the live Calendly widget — never becomes a track. Osas, on that instance: "no, I
  don't see your carousel here."

  There is no autoplay: two cards of copy, and an autoplaying track would move under a
  reader part-way through the body.
*/
(function () {
  'use strict';

  var REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
  /* Matches the stylesheet's breakpoint exactly. Above it the cards are a grid and there is
     no scroll position to track, so every scroll-dependent path below is a no-op. */
  var TRACK_QUERY = window.matchMedia('(max-width: 749px)');

  function NsSideBySide(root) {
    this.root = root;
    this.track = root.querySelector('[data-ns-sbs-track]');
    this.cards = Array.prototype.slice.call(root.querySelectorAll('[data-ns-sbs-card]'));
    this.dots = Array.prototype.slice.call(root.querySelectorAll('[data-ns-sbs-dot]'));
    this.index = 0;
    this.scrollTimer = null;
    this.releaseTimer = null;
    this.programmaticScroll = false;

    if (!this.track || this.cards.length < 2) return;

    this.bind();
  }

  NsSideBySide.prototype.bind = function () {
    var self = this;

    this.dots.forEach(function (dot) {
      dot.addEventListener('click', function () {
        self.goTo(parseInt(dot.dataset.index, 10), true);
      });
    });

    /* Arrow keys move between cards. Bound on the root rather than each dot so the whole
       component responds once the visitor has focus anywhere inside it. */
    this.root.addEventListener('keydown', function (e) {
      if (!TRACK_QUERY.matches) return;
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        self.goTo((self.index + 1) % self.cards.length, true);
      }
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        self.goTo((self.index - 1 + self.cards.length) % self.cards.length, true);
      }
    });

    /* The visitor can flick the track directly, so the dots have to follow the scroll or
       they report the wrong card. Debounced: scroll fires far more often than the active
       card actually changes. */
    this.track.addEventListener('scroll', function () {
      if (!TRACK_QUERY.matches) return;
      /* Ignore the scroll our own goTo() started, or re-deriving the index from a clamped
         target would undo the choice the visitor just made with a dot. */
      if (self.programmaticScroll) return;
      clearTimeout(self.scrollTimer);
      self.scrollTimer = setTimeout(function () { self.syncToScroll(); }, 80);
    }, { passive: true });
  };

  /* aria-current, not aria-selected: these are buttons that move a scroll position, not
     tabs in a tablist, and there is no tabpanel wiring here to justify the tab role. */
  NsSideBySide.prototype.syncDots = function (from, to) {
    if (this.dots[from]) {
      this.dots[from].classList.remove('is-active');
      this.dots[from].removeAttribute('aria-current');
    }
    if (this.dots[to]) {
      this.dots[to].classList.add('is-active');
      this.dots[to].setAttribute('aria-current', 'true');
    }
  };

  /* The scroll offset that puts card `i` at the track's resting position.

     🔴 The leading padding has to come off, the same trap ns-editorial-gallery documents:
     the track carries a padding-inline restoring the design's 16px side inset, and
     offsetLeft is measured from the PADDING edge, so card 0 reports offsetLeft = 16.
     Scrolling to 16 would park it flush against the viewport edge, cancelling the very
     inset that padding exists to create. */
  NsSideBySide.prototype.offsetFor = function (i) {
    var pad = parseFloat(getComputedStyle(this.track).paddingInlineStart) || 0;
    var target = this.cards[i].offsetLeft - this.track.offsetLeft - pad;

    /* Clamp to what the scroller can actually reach: the last card has too little content
       to its right to reach the leading inset, so asking for an unreachable offset would
       leave syncToScroll comparing against a position the track can never hold. */
    var max = this.track.scrollWidth - this.track.clientWidth;
    return Math.max(0, Math.min(target, max));
  };

  /* Which card is nearest the resting position, without scrolling anything: this runs in
     response to the visitor's own scrolling. */
  NsSideBySide.prototype.syncToScroll = function () {
    var left = this.track.scrollLeft;
    var nearest = 0;
    var best = Infinity;

    /* `<=` not `<`, so a tie resolves to the HIGHER index: the trailing card bottoms out at
       the scroller's maximum, and `<` would leave the dots stuck one short of the end. */
    for (var i = 0; i < this.cards.length; i++) {
      var delta = Math.abs(this.offsetFor(i) - left);
      if (delta <= best) { best = delta; nearest = i; }
    }

    if (nearest !== this.index) {
      this.syncDots(this.index, nearest);
      this.index = nearest;
    }
  };

  NsSideBySide.prototype.goTo = function (i, scroll) {
    if (i === this.index || i < 0 || i >= this.cards.length) return;

    this.syncDots(this.index, i);
    this.index = i;

    /* Only below the breakpoint is there a scroll position to move; above it the cards are
       a grid and the dots are hidden, so there is nothing to do. */
    if (scroll && TRACK_QUERY.matches) {
      var self = this;
      this.programmaticScroll = true;
      clearTimeout(this.releaseTimer);
      /* Released once the smooth scroll has settled; the flag only has to outlive the
         scroll events this call generates. */
      this.releaseTimer = setTimeout(function () {
        self.programmaticScroll = false;
      }, REDUCED_MOTION.matches ? 50 : 700);

      this.track.scrollTo({
        left: this.offsetFor(i),
        behavior: REDUCED_MOTION.matches ? 'auto' : 'smooth'
      });
    }
  };

  function init(scope) {
    (scope || document).querySelectorAll('[data-ns-sbs]').forEach(function (el) {
      if (!el.nsSideBySide) el.nsSideBySide = new NsSideBySide(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  // Re-initialise when the section is re-rendered in the theme editor.
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
